/**
 * CollectionsControl: multi-select, orderable collections for the wire editor sidebar.
 * Fetches collections and all_collections via GET wire/:wire_id/series. Displays selected
 * collections as draggable chips (first = primary), and an "Add more" dropdown grouped
 * by collection_type. Persists changes via POST wire/:wire_id/series (update_collections).
 */
import _ from 'lodash';
import React from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import postal from 'postal';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import ErrorBoundary from '../../../../shared/error-boundary';
import Loading from '../../../../shared/loading';
import defaultOnErrorHandler from '../../../../shared/default-on-error-handler';
import move from '../../../../shared/move';
import {
  getWireSeries,
  updateCollections,
} from '../../../../services/wire/edit-services';

const COLLECTION_TYPE_ORDER = ['topic', 'show', 'series'];
const COLLECTION_TYPE_LABELS = {
  topic: 'Topics (Beats)',
  show: 'Shows',
  series: 'Series',
};

const DragHandle = SortableHandle(() => (
  <span
    className="collections-control__drag-handle"
    aria-label="Drag to reorder"
  >
    <i className="fa fa-bars" />
  </span>
));

const SortableChip = SortableElement(({ item, onRemove }) => (
  <div className="collections-control__chip">
    <DragHandle />
    <span className="collections-control__chip-label">
      {item.title}
      {item.is_primary && (
        <span className="collections-control__chip-primary">PRIMARY</span>
      )}
    </span>
    <button
      type="button"
      className="collections-control__chip-remove"
      onClick={() => onRemove(item.id)}
      aria-label={`Remove ${item.title}`}
    >
      <i className="fa fa-times" />
    </button>
  </div>
));

const SortableChipList = SortableContainer(({ items, onRemove }) => (
  <div className="collections-control__chips">
    {items.map((item, index) => (
      <SortableChip
        key={item.id}
        index={index}
        item={item}
        onRemove={onRemove}
      />
    ))}
  </div>
));

function publishPrimaryCollectionChanged(collections) {
  const primaryTitle = collections?.length ? collections[0].title : null;
  postal.publish({
    channel: 'editor',
    topic: 'collections-changed',
    data: primaryTitle,
  });
}

export default class CollectionsControl extends React.Component {
  static propTypes = {
    wireId: PropTypes.number.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      collections: [],
      allCollections: [],
      loading: true,
    };
    this.onSortEnd = this.onSortEnd.bind(this);
    this.removeCollection = this.removeCollection.bind(this);
    this.toggleCollection = this.toggleCollection.bind(this);
    this.formatOptionLabel = this.formatOptionLabel.bind(this);
    this.persistCollections = this.persistCollections.bind(this);
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.wireId !== this.props.wireId) {
      this.load();
    }
  }

  onSortEnd({ oldIndex, newIndex }) {
    this.setState(
      state => ({
        collections: move(state.collections, oldIndex, newIndex).map(
          (c, i) => ({
            ...c,
            is_primary: i === 0,
          }),
        ),
      }),
      () => {
        this.persistCollections();
        publishPrimaryCollectionChanged(this.state.collections);
      },
    );
  }

  removeCollection(id) {
    this.setState(state => {
      const collections = state.collections
        .filter(c => c.id !== id)
        .map((c, i) => ({
          ...c,
          is_primary: i === 0,
        }));
      return { collections };
    }, () => {
      this.persistCollections();
      publishPrimaryCollectionChanged(this.state.collections);
    });
  }

  toggleCollection(selectedOptions) {
    const { allCollections, collections } = this.state;
    const byId = _.keyBy(allCollections, 'id');
    const newIds = (selectedOptions || []).map(o => o.value);
    const existingOrdered = collections.filter(c => newIds.includes(c.id));
    const addedIds = newIds.filter(
      id => !collections.some(c => c.id === id),
    );
    const orderedIds = [...existingOrdered.map(c => c.id), ...addedIds];
    const newCollections = orderedIds
      .map((id, i) => {
        const c = byId[id];
        return c ? { id: c.id, title: c.title, is_primary: i === 0 } : null;
      })
      .filter(Boolean);
    this.setState({ collections: newCollections }, () => {
      this.persistCollections();
      publishPrimaryCollectionChanged(this.state.collections);
    });
  }

  load() {
    const { wireId } = this.props;
    if (!wireId) return;
    this.loading.start();
    getWireSeries(wireId)
      .then(res => {
        const raw = res.data || {};
        const collections = _.sortBy(raw.collections || [], 'position').map(
          c => ({
            id: c.id,
            title: c.title || `#${c.id}`,
            is_primary: c.is_primary,
          }),
        );
        this.setState({
          collections,
          allCollections: raw.all_collections || [],
          loading: false,
        });
      })
      .catch(err => defaultOnErrorHandler(err, 'Error loading collections'))
      .finally(() => this.loading && this.loading.stop());
  }

  persistCollections() {
    const { wireId } = this.props;
    const { collections } = this.state;
    if (!wireId) return;
    const collectionIds = collections.map(c => c.id);
    this.loading.start();
    updateCollections(wireId, collectionIds)
      .catch(err => defaultOnErrorHandler(err, 'Error updating collections'))
      .finally(() => this.loading && this.loading.stop());
  }

  buildGroupedOptions() {
    const { allCollections } = this.state;
    const optionsByType = _.groupBy(
      allCollections,
      c => c.collection_type || 'series',
    );
    return COLLECTION_TYPE_ORDER.map(type => {
      const label = COLLECTION_TYPE_LABELS[type] || type;
      const opts = (optionsByType[type] || []).map(c => ({
        value: c.id,
        label: c.title,
      }));
      return { label, options: opts };
    }).filter(g => g.options.length > 0);
  }

  formatOptionLabel(option) {
    const selected = this.state.collections.some(c => c.id === option.value);
    return (
      <div className="collections-control__option">
        <span
          className={`collections-control__option-checkbox ${selected ? 'collections-control__option-checkbox--checked' : ''}`}
          aria-hidden
        >
          {selected ? (
            <i className="fa fa-check-square-o" />
          ) : (
            <i className="fa fa-square-o" />
          )}
        </span>
        <span className="collections-control__option-label">
          {option.label}
        </span>
      </div>
    );
  }

  render() {
    const { collections } = this.state;
    const groupedOptions = this.buildGroupedOptions();
    const primaryName = collections.length ? collections[0].title : null;

    return (
      <ErrorBoundary>
        <aside className="sidebar-item">
          <form className="--user-facing">
            <section className="form-group collections-control">
              <h3 className="collections-control__title">COLLECTIONS</h3>
              {primaryName ? (
                <p className="collections-control__helper">
                  Note: Primary collection -
                  {' '}
                  {primaryName}
                  .
                  {' '}
                  <strong>Drag to reorder</strong>
                  .
                </p>
              ) : (
                <p className="collections-control__helper">
                  Note: The first selected collection is the primary.
                  {' '}
                  <strong>Drag to reorder</strong>
                  ..
                </p>
              )}
              <div className={`${this.state.loading ? 'collections-loading' : 'collections-loaded'}`}>
                <Loading variant="sip" ref={el => (this.loading = el)} />
              </div>
              {collections.length > 0 && (
                <SortableChipList
                  items={collections}
                  onSortEnd={this.onSortEnd}
                  onRemove={this.removeCollection}
                  useDragHandle
                  helperClass="collections-control__chip--dragging"
                />
              )}
              <div className="collections-control__add">
                <Select
                  className="select-container collections-control__select"
                  classNamePrefix="select"
                  placeholder="Add more collections..."
                  options={groupedOptions}
                  value={collections.map(c => ({
                    value: c.id,
                    label: c.title,
                  }))}
                  onChange={this.toggleCollection}
                  formatGroupLabel={group => (
                    <span className="collections-control__group-label">
                      {group.label}
                    </span>
                  )}
                  formatOptionLabel={this.formatOptionLabel}
                  isMulti
                  controlShouldRenderValue={false}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  backspaceRemovesValue={false}
                  escapeClearsValue={false}
                  components={{
                    MultiValue: () => null,
                    ClearIndicator: () => null,
                  }}
                  isSearchable
                />
              </div>
            </section>
          </form>
        </aside>
      </ErrorBoundary>
    );
  }
}
